import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { CheckCircle2, ChevronLeft, ChevronRight, CloudRain, Compass, Droplets, Gem, HandHelping, HeartPulse, LifeBuoy, Loader2, Moon, Pause, Play, Plane, Search, Shirt, Sunrise, Tent, Users, Utensils } from 'lucide-react';
import type { ComponentType } from 'react';
import { MosqueIcon, TasbihIcon } from '@/components/islamic/Ornaments';
import { DISTRESS, HadithCard, type DistressDua } from '@/features/adhkar/Virtues';
import { AyatCard } from '@/features/adhkar/Sins';
import { IconButton, LargeTitle, haptic } from '@/components/ui';
import { AdhkarArabic, AdhkarDisplayButton, NeedPill, WithNeed } from '@/features/adhkar/AdhkarText';
import { useRuqyah, type QueueTrack } from '@/features/adhkar/ruqyahPlayer';
import { GROUPS, ISTIKHARAH, MORNING_EVENING, loadHisn, matches, type HisnChapter, type HisnDua } from './hisn';

/* An icon for each group of chapters (hisn.ts GROUPS, by title): tap it to see that group. */
const GROUP_ICON: Record<string, ComponentType<{ size?: number; className?: string }>> = {
  'Waking, sleeping & the night': Moon, 'Morning & evening': Sunrise, 'Clothes & the home': Shirt, Purification: Droplets,
  'The mosque & prayer': MosqueIcon, 'Worry, hardship & protection': LifeBuoy, 'Children, illness & death': HeartPulse,
  'Weather & nature': CloudRain, 'Food & fasting': Utensils, Marriage: Gem, 'Gatherings & manners': Users, Travel: Plane,
  'Hajj, ʿUmrah & sacrifice': Tent, 'Repentance & remembrance': TasbihIcon,
};

const useHisn = () => useQuery({ queryKey: ['hisn'], queryFn: loadHisn, staleTime: Infinity });
const ALBUM = 'Duʿāʾ';

export function DuasHome() {
  const nav = useNavigate();
  const { data } = useHisn();
  const [q, setQ] = useState('');
  const [params, setParams] = useSearchParams();
  const openGroup = GROUPS.find((g) => g.title === params.get('g')) ?? null;
  const byId = useMemo(() => new Map((data?.chapters ?? []).map((c) => [c.id, c])), [data]);
  const istikharah = byId.get(ISTIKHARAH);
  return (
    <div className="page-pad">
      <LargeTitle title="Duʿāʾ" ar="الأدعية" subtitle="Ḥiṣn al-Muslim — Fortress of the Muslim" right={<IconButton label="Back" onClick={() => nav('/adhkar')}><ChevronLeft size={20} /></IconButton>} />
      <div className="space-y-4 px-4">
        <label className="flex items-center gap-2 rounded-2xl bg-surface px-4 py-3">
          <Search size={18} className="shrink-0 text-muted" aria-hidden />
          <input type="search" aria-label="Search duʿāʾ" placeholder="Search — e.g. travel, anxiety, rain" value={q} onChange={(e) => setQ(e.target.value)} className="min-w-0 flex-1 bg-transparent text-[16px] outline-none" />
        </label>

        {!data ? <div className="grid place-items-center py-10 text-muted"><Loader2 className="animate-spin" /></div> : (
          <>
            {!q && (
              <Link to="/duas/distress" className="press flex items-center gap-4 rounded-[22px] bg-surface p-5 shadow-[inset_0_0_0_1px_var(--card-edge)]">
                <span className="grid h-14 w-14 shrink-0 place-items-center rounded-full bg-accent/15 text-accent"><HandHelping size={26} /></span>
                <span className="min-w-0 flex-1">
                  <span className="block text-[18px] font-bold">In distress & great need</span>
                  <span className="block font-kufi text-[16px] text-gold">عند الكرب والشدة</span>
                  <span className="block text-[13px] text-muted">What the Prophet ﷺ said in hardship, grief and need — and when duʿāʾ is answered</span>
                </span>
                <ChevronRight className="shrink-0 text-faint" />
              </Link>
            )}
            {!q && (
              <div className="grid grid-cols-3 gap-3" role="tablist" aria-label="Duʿāʾ topics">
                {GROUPS.map((g) => {
                  const Icon = GROUP_ICON[g.title] ?? Sunrise;
                  const on = openGroup?.title === g.title;
                  return (
                    <button key={g.title} type="button" role="tab" aria-selected={on} aria-label={g.title}
                      onClick={() => setParams(on ? {} : { g: g.title })}
                      className={`press flex flex-col items-center gap-2 rounded-[20px] p-3 text-center ${on ? 'bg-surface shadow-[0_0_0_2px_var(--gold)]' : 'bg-surface/70'}`}>
                      <span className="grid h-12 w-12 place-items-center rounded-full bg-gold/15 text-gold"><Icon size={24} /></span>
                      <span className="text-[12px] font-semibold leading-tight">{g.title}</span>
                    </button>
                  );
                })}
              </div>
            )}
            {!q && !openGroup && <p className="px-1 text-center text-[14px] text-muted">Tap a topic to see its duʿāʾ, or search above.</p>}
            {!q && istikharah && (!openGroup || openGroup.ids.includes(ISTIKHARAH)) && (
              <Link to={`/duas/${ISTIKHARAH}`} className="press flex items-center gap-4 rounded-[22px] bg-surface p-5 shadow-[inset_0_0_0_1px_var(--card-edge)]">
                <span className="grid h-14 w-14 shrink-0 place-items-center rounded-full bg-gold/15 text-gold"><Compass size={26} /></span>
                <span className="min-w-0 flex-1">
                  <span className="block text-[18px] font-bold">Istikhārah</span>
                  <span className="block font-kufi text-[16px] text-gold">{istikharah.titleAr}</span>
                  <span className="block text-[13px] text-muted">Seeking Allah’s counsel before a decision — two rakʿahs, then this duʿāʾ</span>
                </span>
                <ChevronRight className="shrink-0 text-faint" />
              </Link>
            )}
            {GROUPS.filter((g) => q || openGroup?.title === g.title).map((g) => {
              const chapters = g.ids.map((id) => byId.get(id)).filter((c): c is HisnChapter => !!c && matches(c, q));
              if (!chapters.length) return null;
              return (
                <section key={g.title}>
                  <h2 className="px-1 pb-2 text-[13px] font-semibold uppercase tracking-wide text-muted">{g.title}</h2>
                  <div className="overflow-hidden rounded-[22px] bg-surface">
                    {chapters.map((c) => (
                      <Link key={c.id} to={c.id === MORNING_EVENING ? '/adhkar' : `/duas/${c.id}`} className="press flex items-center gap-3 border-b border-hairline px-4 py-3 last:border-0">
                        <span className="min-w-0 flex-1">
                          <span className="block text-[15px] font-semibold leading-snug">{c.title}</span>
                          <span className="block truncate font-arabic text-[14px] text-muted" dir="rtl">{c.titleAr}</span>
                        </span>
                        <span className="shrink-0 text-[12px] tabular-nums text-faint">{c.items.length}</span>
                        <ChevronRight size={18} className="shrink-0 text-faint" />
                      </Link>
                    ))}
                  </div>
                </section>
              );
            })}
            <p className="px-1 pb-4 text-[12px] leading-relaxed text-faint">{data.source}.</p>
          </>
        )}
      </div>
    </div>
  );
}

const trackOf = (c: HisnChapter, d: HisnDua, i: number): QueueTrack => ({ id: `hisn-${d.id}`, title: c.items.length > 1 ? `${c.title} (${i + 1})` : c.title, reciter: 'Ḥiṣn al-Muslim', url: d.audio, seconds: 0 });

export function DuaChapter() {
  const { id = '' } = useParams();
  const nav = useNavigate();
  const { data } = useHisn();
  const [showTr, setShowTr] = useState(true);
  const c = data?.chapters.find((x) => x.id === Number(id));
  const r = useRuqyah();
  if (!data) return <div className="grid min-h-dvh place-items-center text-muted"><Loader2 className="animate-spin" /></div>;
  if (!c) return <div className="page-pad px-6 pt-20 text-center text-muted">Not found. <Link to="/duas" className="font-semibold text-accent">All duʿāʾ</Link></div>;
  const playable = c.items.filter((d) => d.audio && d.ar);
  const queue = playable.map((d) => trackOf(c, d, c.items.indexOf(d)));
  const current = r.status !== 'idle' ? r.queue[r.index]?.id : null;
  return (
    <div className="page-pad !pt-0">
      <header className="glass sticky top-0 z-30 flex items-center gap-2 px-3 pb-2" style={{ paddingTop: 'calc(var(--safe-top) + 8px)' }}>
        <IconButton label="Back" onClick={() => nav('/duas')}><ChevronLeft size={20} /></IconButton>
        <div className="min-w-0 flex-1 text-center">
          <div className="truncate text-[15px] font-bold">{c.title}</div>
          <div className="truncate font-arabic text-[13px] text-gold" dir="rtl">{c.titleAr}</div>
        </div>
        <AdhkarDisplayButton showTr={showTr} setShowTr={setShowTr} />
      </header>
      <div className="mx-auto max-w-[720px] space-y-3 px-4 pt-4">
        {queue.length > 1 && (
          <button type="button" className="press flex w-full items-center justify-center gap-2 rounded-2xl bg-accent py-3 text-[15px] font-semibold text-accent-ink"
            onClick={() => { haptic(); r.playQueue(queue, 0, ALBUM); }}><Play size={17} /> Play all ({queue.length})</button>
        )}
        {c.items.map((d, i) => <DuaCard key={d.id} d={d} track={d.audio && d.ar ? trackOf(c, d, i) : null} playingId={current} showTr={showTr} queue={queue} />)}
        <p className="px-1 pb-4 text-[12px] text-faint">{data.source}, chapter {c.id}.</p>
      </div>
    </div>
  );
}

function DuaCard({ d, track, playingId, showTr, queue }: { d: HisnDua; track: QueueTrack | null; playingId: string | null | undefined; showTr: boolean; queue: QueueTrack[] }) {
  const [n, setN] = useState(0);
  const r = useRuqyah();
  const on = !!track && playingId === track.id;
  const done = n >= d.repeat;
  const counted = d.repeat > 1;
  return (
    <div className={`rounded-[22px] p-5 ${done && counted ? 'bg-accent/10' : 'bg-surface'}`}>
      {d.intro && <p className="mb-4 rounded-2xl bg-accent/10 px-4 py-3 text-[14px] font-medium leading-relaxed text-ink">{d.intro}</p>}
      {d.note && (
        <details className="mb-4 rounded-2xl bg-fill px-4 py-3 text-[14px] leading-relaxed text-muted">
          <summary className="cursor-pointer font-semibold text-ink">{d.noteTitle ?? 'About this duʿāʾ'}</summary>
          <p className="mt-2">{d.note}</p>
          <p className="mt-2 text-[12px] text-faint">The audio recites the full hadith, including this narration.</p>
        </details>
      )}
      {d.ar && <AdhkarArabic ar={d.ar} />}
      {showTr && <>
        {d.tr && <p className="mt-3 text-[14px] italic leading-relaxed text-muted"><WithNeed text={d.tr} /></p>}
        <p className={`${d.ar ? 'mt-2' : ''} text-[15px] leading-relaxed`}><WithNeed text={d.en} /></p>
      </>}
      {(track || counted) && (
        <div className="mt-4 flex items-center justify-between gap-3">
          {track ? (
            <button type="button" aria-label={on && r.status === 'playing' ? 'Pause duʿāʾ' : 'Play duʿāʾ'} className="press flex items-center gap-1.5 rounded-full bg-gold/15 px-3 py-1.5 text-[13px] font-semibold text-gold"
              onClick={() => { haptic(); if (on) r.toggle(); else r.playQueue(queue, Math.max(0, queue.findIndex((t) => t.id === track.id)), ALBUM); }}>
              {on && r.status === 'loading' ? <Loader2 size={15} className="animate-spin" /> : on && r.status === 'playing' ? <Pause size={15} /> : <Play size={15} />} Listen
            </button>
          ) : <span />}
          {counted && (
            <button type="button" aria-label={`Count ${n} of ${d.repeat}`} disabled={done} onClick={() => { haptic(n + 1 === d.repeat ? 30 : 8); setN(n + 1); }}
              className={`press flex h-12 min-w-12 items-center justify-center rounded-full px-3 text-[17px] font-bold tabular-nums ${done ? 'bg-accent text-accent-ink' : 'bg-fill'}`}>
              {done ? <CheckCircle2 size={22} /> : `${n}/${d.repeat}`}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/* In distress & great need — a duʿāʾ to follow step by step, in the order the Prophet ﷺ taught (praise,
   ṣalawāt, then asking), using his own words for distress: Ḥiṣn al-Muslim's vowelled Arabic and audio, each
   with the hadith it comes from, quoted with the scholars' grading (scripts/fetch-virtues.mjs builds it). */
function Evidence({ ids, label, source }: { ids: string[]; label: string; source?: string }) {
  if (!ids.length && !source) return null;
  return (
    <details className="rounded-2xl bg-fill/60 px-4 py-2 text-[14px]">
      <summary className="cursor-pointer py-1 text-[13px] font-semibold text-muted">{label}</summary>
      <div className="space-y-2 pb-2 pt-1">
        {ids.map((id) => <HadithCard key={id} id={id} />)}
        {source && <p className="text-[12px] leading-snug text-muted">{source}</p>}
      </div>
    </details>
  );
}

export function DistressPage() {
  const nav = useNavigate();
  const { data } = useHisn();
  const [showTr, setShowTr] = useState(true);
  const r = useRuqyah();
  const byDua = useMemo(() => new Map((data?.chapters ?? []).flatMap((c) => c.items.map((d) => [d.id, d] as const))), [data]);
  if (!data) return <div className="grid min-h-dvh place-items-center text-muted"><Loader2 className="animate-spin" /></div>;
  const duaOf = (x: DistressDua): HisnDua => (x.hisn ? byDua.get(x.hisn)! : { id: -1, ar: x.ar ?? '', tr: x.tr ?? '', en: x.en ?? '', repeat: 1, audio: '' });
  const all = DISTRESS.steps.flatMap((st) => st.duas.map((x) => ({ x, d: duaOf(x) })));
  const queue: QueueTrack[] = all.filter(({ d }) => d.audio && d.ar).map(({ x, d }) => ({ id: `hisn-${d.id}`, title: x.title, reciter: 'Ḥiṣn al-Muslim', url: d.audio, seconds: 0 }));
  const current = r.status !== 'idle' ? r.queue[r.index]?.id : null;
  return (
    <div className="page-pad !pt-0">
      <header className="glass sticky top-0 z-30 flex items-center gap-2 px-3 pb-2" style={{ paddingTop: 'calc(var(--safe-top) + 8px)' }}>
        <IconButton label="Back" onClick={() => nav('/duas')}><ChevronLeft size={20} /></IconButton>
        <div className="min-w-0 flex-1 text-center">
          <div className="truncate text-[15px] font-bold">In distress & great need</div>
          <div className="truncate font-kufi text-[13px] text-gold" dir="rtl">عند الكرب والشدة</div>
        </div>
        <AdhkarDisplayButton showTr={showTr} setShowTr={setShowTr} />
      </header>
      <div className="mx-auto max-w-[720px] space-y-3 px-4 pt-4">
        <p className="rounded-2xl bg-accent/10 px-4 py-3 text-[15px] leading-relaxed">{DISTRESS.intro}</p>

        {/* He is near, and He answers */}
        <section id="assurance" className="space-y-3">
          {DISTRESS.assurance.ayat.map((k) => <AyatCard key={k} keys={[k]} />)}
          {DISTRESS.assurance.h.map((id) => <HadithCard key={id} id={id} />)}
        </section>

        <section id="before" className="space-y-2 pt-3">
          <h2 className="px-1 text-[17px] font-bold">{DISTRESS.before.title}</h2>
          <ul className="space-y-2 rounded-2xl bg-surface p-4 text-[15px] leading-relaxed">
            {DISTRESS.before.points.map((pt) => <li key={pt} className="flex gap-2"><CheckCircle2 size={18} className="mt-1 shrink-0 text-accent" /><span>{pt}</span></li>)}
          </ul>
          <Evidence ids={DISTRESS.before.h} label="Where this comes from" />
        </section>

        <ol className="space-y-6 pt-4">
          {DISTRESS.steps.map((st, n) => (
            <li key={st.id} id={`step-${st.id}`} className="space-y-3">
              <div className="flex items-start gap-3">
                <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-accent text-[16px] font-bold text-accent-ink" aria-hidden>{n + 1}</span>
                <div className="min-w-0 flex-1">
                  <h2 className="text-[18px] font-bold leading-snug"><span className="sr-only">Step {n + 1}: </span>{st.title}</h2>
                  <p className="mt-0.5 text-[14px] leading-snug text-muted">{st.why}</p>
                </div>
              </div>
              {st.proof && <Evidence ids={st.proof} label="Why this step" />}
              {st.duas.map((x) => {
                const d = duaOf(x);
                return (
                  <div key={x.id} id={x.id} className="space-y-2">
                    <h3 className="px-1 text-[14px] font-semibold text-gold">{x.title}</h3>
                    <DuaCard d={d} track={d.audio && d.ar ? queue.find((t) => t.id === `hisn-${d.id}`) ?? null : null} playingId={current} showTr={showTr} queue={queue} />
                    <Evidence ids={x.h} source={x.source} label="Source & virtue" />
                  </div>
                );
              })}
              {st.need && (
                <div className="rounded-[22px] border-2 border-dashed border-gold/50 p-5 text-center">
                  <div className="text-[16px] font-bold">Now ask for your need</div>
                  <p className="mt-1 text-[14px] leading-relaxed text-muted">In your own words and your own language — openly, humbly, and three times.</p>
                  <div className="mt-2"><NeedPill lang="en" /></div>
                </div>
              )}
              {st.ayat?.map((k) => <AyatCard key={k} keys={[k]} />)}
              {st.id === 'close' && <a href="#step-salawat" className="press block rounded-2xl bg-surface px-4 py-3 text-center text-[14px] font-semibold text-accent">Back to the ṣalawāt (step 2)</a>}
            </li>
          ))}
        </ol>

        <section id="after" className="space-y-3 pt-4">
          <h2 className="px-1 text-[17px] font-bold">{DISTRESS.after.title}</h2>
          <p className="px-1 text-[14px] leading-relaxed text-muted">{DISTRESS.after.why}</p>
          {DISTRESS.after.ayat.map((k) => <AyatCard key={k} keys={[k]} />)}
          {DISTRESS.after.h.map((id) => <HadithCard key={id} id={id} />)}
        </section>
        <p className="px-1 pb-4 text-[12px] text-faint">Duʿāʾ text and audio: {data.source}. Hadith quoted from sunnah.com with the named scholars’ gradings; Qurʾān translation: Saheeh International.</p>
      </div>
    </div>
  );
}
